import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatEUR, formatDate, API, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, FileText, Send, Trash2, Repeat, Download, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  borrador: "bg-slate-100 text-slate-700",
  enviado: "bg-blue-100 text-blue-700",
  aceptado: "bg-emerald-100 text-emerald-700",
  rechazado: "bg-rose-100 text-rose-700",
  convertido: "bg-indigo-100 text-indigo-700",
};

export default function PresupuestosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openPdf = async (id) => {
    const token = localStorage.getItem("chronovia_token");
    window.open(`${API}/quotes/${id}/pdf?_t=${Date.now()}`, "_blank");
    // Note: PDF endpoint expects auth token — for simplicity fetch as blob
    try {
      const r = await fetch(`${API}/quotes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      // fallback already opened
    }
  };

  const sendQuote = async (id) => {
    try {
      const { data } = await api.post(`/quotes/${id}/send`);
      if (data.sent) {
        toast.success("Presupuesto enviado al cliente");
        load();
      } else {
        toast.error("No se pudo enviar (revisa el email del cliente)");
      }
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const convert = async (id) => {
    try {
      const { data } = await api.post(`/quotes/${id}/convert`);
      toast.success(`Convertido a factura ${data.number}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const setStatus = async (id, status) => {
    await api.put(`/quotes/${id}/status`, { status });
    toast.success("Estado actualizado");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este presupuesto?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div className="space-y-6" data-testid="presupuestos-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Presupuestos
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Crea, envía y convierte presupuestos aprobados en facturas.
          </p>
        </div>
        <Link to="/app/presupuestos/nuevo">
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Nuevo presupuesto
          </Button>
        </Link>
      </div>

      <Card className="p-5">
        {loading ? (
          <div className="text-center py-10 text-slate-500">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-14">
            <FileText className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">
              No has creado presupuestos todavía.
            </p>
            <Link to="/app/presupuestos/nuevo">
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700"
                data-testid="empty-new-quote"
              >
                Crear primer presupuesto
              </Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Número</th>
                  <th className="py-2 pr-4">Cliente</th>
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2 pr-4 text-right">Total</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr
                    key={q.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`quote-row-${q.id}`}
                  >
                    <td className="py-3 pr-4 font-medium text-slate-900">
                      {q.number}
                    </td>
                    <td className="py-3 pr-4 text-slate-700">
                      {q.client_snapshot?.name}
                    </td>
                    <td className="py-3 pr-4 text-slate-600">
                      {formatDate(q.issue_date)}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded ${
                          STATUS_MAP[q.status] || STATUS_MAP.borrador
                        }`}
                      >
                        {q.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right font-medium text-slate-900">
                      {formatEUR(q.total)}
                    </td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`quote-actions-${q.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openPdf(q.id)}>
                            <Download className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendQuote(q.id)}>
                            <Send className="w-4 h-4 mr-2" /> Enviar al cliente
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setStatus(q.id, "aceptado")}>
                            <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />{" "}
                            Marcar aceptado
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatus(q.id, "rechazado")}>
                            Marcar rechazado
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => convert(q.id)}
                            disabled={q.status === "convertido"}
                          >
                            <Repeat className="w-4 h-4 mr-2 text-blue-600" />{" "}
                            Convertir a factura
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => remove(q.id)}
                            className="text-rose-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
